import type { PdfSectionScope } from "./types/pdf-templates";

/**
 * QPBI-321 — portfolio-scoped conditional-display sources.
 *
 * `portfolioType` and `scheduleType` live on `goal_portfolio` (QP-150), so
 * conditions on them are only evaluable where a current portfolio exists:
 * flow components/layouts on `portfolio` pages, and PDF components/layouts
 * inside `per_portfolio` sections. They are never resolved at goal or global
 * level — there is no fallback.
 *
 * These replace the retired goal-labelled sources `__goal_portfolio_type__`
 * and `__goal_schedule_type__` (removed from all authoring surfaces and
 * evaluators; production data was cleared of them before this shipped).
 */
export const PORTFOLIO_TYPE_SOURCE = "__portfolio_type__";
export const PORTFOLIO_SCHEDULE_SOURCE = "__portfolio_schedule_type__";

/**
 * QL-120 — goal-type conditional-display source.
 *
 * Resolves to the goal's stored `goalType` (a goal-type row id from the
 * flow's portfolio collection, or GOAL_TYPE_NONE). Only evaluable where a
 * current goal exists: flow pages/layouts/components on `goal` and
 * `portfolio` pages, and PDF components/layouts inside per-goal /
 * per-portfolio sections. Never resolved at global level — no fallback.
 *
 * Rules on this source use the set operators (`in` / `not_in`) with a JSON
 * array of goal-type ids as the value; plain equality is not offered.
 */
export const GOAL_TYPE_SOURCE = "__goal_type__";
/**
 * The `goalType` value the product writes on every freshly created goal
 * (flow-instance-service / public-access-service). Offered in the authoring
 * picker as "No type" so a rule can target goals whose type is still unchosen.
 */
export const GOAL_TYPE_NONE = "other";

/**
 * QL-161 — scope model for conditional-display sources.
 *
 * A condition's source may only be used on a container whose scope is at
 * least as specific as the source (`global < goal < portfolio`):
 *
 * | Container                                            | Allowed sources          |
 * |------------------------------------------------------|--------------------------|
 * | flow `global` page / PDF `global` section / attachment | global                 |
 * | flow `goal` page / PDF `per_goal` section            | global + goal            |
 * | flow `portfolio` page / PDF `per_portfolio` section  | global + goal + portfolio |
 *
 * This registry + predicate is the single source of truth shared by the flow
 * picker, the PDF pickers, the AI conditionals tool and the server-side
 * guard, so they cannot drift apart. It records scope only — no PDF-only /
 * flow-only flags (deliberately minimal).
 */
export type ConditionScope = "global" | "goal" | "portfolio";

/** Prefix of client custom-field sources: `__custom_field_<uuid>`. */
export const CUSTOM_FIELD_SOURCE_PREFIX = "__custom_field_";

/**
 * QL-13 — prefix of quiz-result sources: `__quiz_result_<quiz uuid>`.
 *
 * Keyed by id, not by name, so renaming a quiz cannot break a rule that
 * already references it. Values are the three `QuizResultStatus` strings.
 * Always global: a quiz belongs to the tenant, not to a goal or a portfolio.
 */
export const QUIZ_RESULT_SOURCE_PREFIX = "__quiz_result_";

/**
 * QL-361 — prefix of quiz-score sources: `__quiz_score_<quiz uuid>`, a sibling
 * of the result source keyed by the same quiz component id.
 */
export const QUIZ_SCORE_SOURCE_PREFIX = "__quiz_score_";

/**
 * QL-361 — prefix of product-assessment sources:
 * `__pa_<productTypeId>_{quiz|questionnaire|combined}_status`,
 * `__pa_<productTypeId>_{quiz|questionnaire}_score` and, for a simple
 * (declaration-only) type, `__pa_<productTypeId>_simple_status` (QL-541).
 *
 * Always global: the assessment record is stored once per flow instance. The
 * id helpers and the value reader live in `assessment-condition-sources`.
 */
export const PRODUCT_ASSESSMENT_SOURCE_PREFIX = "__pa_";

// Client / instance (global) sentinels.
export const CLIENT_TYPE_SOURCE = "__client_type__";
export const CLIENT_COUNTRY_SOURCE = "__client_country__";

/**
 * QL-217 — the value a client-record rule (`__client_type__`,
 * `__client_country__`) compares against: the raw `client.type` (a Group
 * client is "Group", never mapped to Person) and the ISO country code. One
 * reader for the advisor wizard, the public flow and the completion engine,
 * so a block or tab the client can't see is never required on submit.
 * Returns undefined for any other source.
 */
export function clientRecordConditionValue(
  sourceFieldId: string,
  client: { type?: string | null; country?: string | null } | null | undefined,
): string | undefined {
  if (sourceFieldId === CLIENT_TYPE_SOURCE) return client?.type ?? "";
  if (sourceFieldId === CLIENT_COUNTRY_SOURCE) return client?.country ?? "";
  return undefined;
}

/** QL-217 — the sources `clientRecordConditionValue` resolves. */
export const CLIENT_RECORD_SOURCES = [CLIENT_TYPE_SOURCE, CLIENT_COUNTRY_SOURCE] as const;

export const CLASSIFICATION_TYPE_SOURCE = "__classification_type__";
export const TRAPETS_PEP_SOURCE = "__trapets_pep__";
export const TRAPETS_SANCTIONED_SOURCE = "__trapets_sanctioned__";
export const SUMSUB_VERIFIED_SOURCE = "__sumsub_verified__";
// QL-68 — the ISO 3166-1 alpha-3 issuing country of the document Sumsub
// verified (`info.country` on the applicant; Sumsub's own field, NOT the
// client's country — the two diverge whenever the advisor keeps the client's
// record as it was). Rules store the alpha-3 code as Sumsub reports it, so the
// comparison never runs through a translation table.
export const SUMSUB_DOCUMENT_COUNTRY_SOURCE = "__sumsub_document_country__";
export const FLOW_STATUS_SOURCE = "__flow_status__";
export const EXPECTING_FINANCIAL_CHANGES_SOURCE = "__expecting_financial_changes__";

/**
 * QL-774 — "is this session a follow-up?" as a conditional source, so a tenant
 * that runs one flow for both new advice and follow-ups can tailor pages,
 * blocks and PDF content to each.
 *
 * Resolves to "true" when any portfolio of the instance carries follow-up
 * lineage (`isFollowupPortfolio`: a source account or a source savings plan),
 * "false" otherwise. That is the same instance-level question the completion
 * step asks (`instanceHasFollowupParticipants`), so a session the bank
 * degrades to new advice (its source deleted mid-flight) reads "false" here
 * too. Global: the answer is the instance's, whatever goal or portfolio is in
 * context. Enum source with `equals` / `not_equals`.
 */
export const FOLLOWUP_MODE_SOURCE = "__followup_mode__";

/**
 * The `__followup_mode__` value from the goals the evaluators already hold.
 * Each goal's slim portfolio list carries a server-derived `isFollowup`, so the
 * advisor wizard, the public flow and the completion engine read one answer.
 */
export function followupModeConditionValue(
  goals:
    | ReadonlyArray<{ portfolios?: ReadonlyArray<{ isFollowup?: boolean }> | null }>
    | null
    | undefined,
): "true" | "false" {
  return (goals ?? []).some((g) => (g.portfolios ?? []).some((p) => p.isFollowup))
    ? "true"
    : "false";
}

// Goal sentinels (see GOAL_TYPE_SOURCE above).
export const GOAL_RISK_SCORE_SOURCE = "__goal_risk_score__";
export const GOAL_HAS_RISK_SOURCE = "__goal_has_risk__";
/**
 * QL-375 group 10 — "does this goal have a mandate in force?" as a PDF
 * conditional source, modelled on `__goal_has_risk__`. Resolves to the goal's
 * `goal_has_mandate` value ("true" / "false"), emitted for EVERY goal by the
 * PDF builder so a rule can target either state. Offered in the PDF editor's
 * per-goal and per-portfolio sections; the flow-side evaluators do not resolve
 * it (a flow rule on it reads as absent and hides).
 */
export const GOAL_HAS_MANDATE_SOURCE = "__goal_has_mandate__";
/**
 * QL-375 group 10b — the portfolio's own reading of the mandate, PDF-side
 * sources for per-portfolio sections: whether this portfolio carries its own
 * verdict (portfolio-level blocking and a decided portfolio) and what it is.
 * Emitted for every goal × portfolio by the PDF builder ("false" /
 * "unchecked" when there is none).
 */
export const PORTFOLIO_HAS_MANDATE_CHECK_SOURCE = "__portfolio_has_mandate_check__";
export const PORTFOLIO_MANDATE_STATUS_SOURCE = "__portfolio_mandate_status__";
export const PORTFOLIO_MANDATE_STATUS_VALUES = [
  "ok",
  "accepted",
  "deviation",
  "not_accepted",
  "unchecked",
] as const;

/**
 * QL-72 — "does this portfolio use a hand-picked model portfolio other than the
 * one the risk score matches?" as a PDF conditional source for per-portfolio
 * sections. Resolved by `modelPortfolioDeviation` (model-portfolio-selection);
 * emitted for every goal × portfolio by the PDF builder; the flow-side
 * evaluators do not resolve it. Enum source: `equals` / `not_equals` only.
 */
export const PORTFOLIO_MODEL_DEVIATION_SOURCE = "__portfolio_model_deviation__";
export const PORTFOLIO_MODEL_DEVIATION_VALUES = ["yes", "no", "not_model"] as const;
export type PortfolioModelDeviation = (typeof PORTFOLIO_MODEL_DEVIATION_VALUES)[number];

/**
 * QL-71 — "was this goal's risk score capped?" (QP-180) as a conditional source.
 *
 * Resolves to the goal's `riskScoreCapState`, which the goal service derives
 * server-side: `yes` and `no` come from the goal's frozen submit-time columns
 * (`isRiskScoreCapped`, and `riskScoreCap` — `no` needs the cap to have been
 * applicable at submit), while the flow's risk model and the client's custom
 * field are read live and can only turn the state into `no_client_field_data`.
 * Client-side derivation is not an option — the public flow session payload
 * carries no client data at all, and shipping the cap config plus the client's
 * field value into a token-accessed page to answer one boolean is not a trade
 * worth making.
 *
 * Enum source: `equals` / `not_equals` only.
 *
 * With no goal in scope the evaluators disagree — flow treats the value as ""
 * (`not_equals` shows), PDF hides for both. Only rules that bypass the QL-161
 * scope check (AI-created, legacy) can hit this.
 */
export const GOAL_RISK_SCORE_CAPPED_SOURCE = "__goal_risk_score_capped__";

/**
 * The four values the source emits. `no` means the cap was tested at submit and
 * did not lower the score. `no_client_field_data` means the cap could not be
 * assessed — no cap configured on the model, configured but the client has no
 * value for the cap field, or the cap was not applicable at submit (no cap then,
 * or the client's value did not trigger it) — and is a common state, not an
 * edge case: an untoggled boolean client field simply stores no row.
 */
export const RISK_SCORE_CAP_STATES = [
  "yes",
  "no",
  "no_risk_assessment",
  "no_client_field_data",
] as const;

export type RiskScoreCapState = (typeof RISK_SCORE_CAP_STATES)[number];

/** Sentinel source id → the scope where it is evaluable. */
export const SENTINEL_SOURCE_SCOPES: Readonly<Record<string, ConditionScope>> = {
  [CLIENT_TYPE_SOURCE]: "global",
  [CLIENT_COUNTRY_SOURCE]: "global",
  [CLASSIFICATION_TYPE_SOURCE]: "global",
  [TRAPETS_PEP_SOURCE]: "global",
  [TRAPETS_SANCTIONED_SOURCE]: "global",
  [SUMSUB_VERIFIED_SOURCE]: "global",
  [SUMSUB_DOCUMENT_COUNTRY_SOURCE]: "global",
  [FLOW_STATUS_SOURCE]: "global",
  [EXPECTING_FINANCIAL_CHANGES_SOURCE]: "global",
  [FOLLOWUP_MODE_SOURCE]: "global",
  [GOAL_RISK_SCORE_SOURCE]: "goal",
  [GOAL_HAS_RISK_SOURCE]: "goal",
  [GOAL_HAS_MANDATE_SOURCE]: "goal",
  [GOAL_RISK_SCORE_CAPPED_SOURCE]: "goal",
  [GOAL_TYPE_SOURCE]: "goal",
  [PORTFOLIO_TYPE_SOURCE]: "portfolio",
  [PORTFOLIO_SCHEDULE_SOURCE]: "portfolio",
  [PORTFOLIO_HAS_MANDATE_CHECK_SOURCE]: "portfolio",
  [PORTFOLIO_MANDATE_STATUS_SOURCE]: "portfolio",
  [PORTFOLIO_MODEL_DEVIATION_SOURCE]: "portfolio",
};

const SCOPE_RANK: Record<ConditionScope, number> = { global: 0, goal: 1, portfolio: 2 };

/** `rank(source) <= rank(container)` — the whole QL-161 rule. */
export function isConditionAllowedInScope(
  sourceScope: ConditionScope,
  containerScope: ConditionScope,
): boolean {
  return SCOPE_RANK[sourceScope] <= SCOPE_RANK[containerScope];
}

export function isCustomFieldSource(sourceFieldId: string): boolean {
  return sourceFieldId.startsWith(CUSTOM_FIELD_SOURCE_PREFIX);
}

export function isQuizResultSource(sourceFieldId: string): boolean {
  return sourceFieldId.startsWith(QUIZ_RESULT_SOURCE_PREFIX);
}

export function isQuizScoreSource(sourceFieldId: string): boolean {
  return sourceFieldId.startsWith(QUIZ_SCORE_SOURCE_PREFIX);
}

export function isProductAssessmentSource(sourceFieldId: string): boolean {
  return sourceFieldId.startsWith(PRODUCT_ASSESSMENT_SOURCE_PREFIX);
}

/** `__quiz_result_<id>` -> `<id>`. Returns "" for anything else. */
export function quizIdFromResultSource(sourceFieldId: string): string {
  return isQuizResultSource(sourceFieldId)
    ? sourceFieldId.slice(QUIZ_RESULT_SOURCE_PREFIX.length)
    : "";
}

/** `__quiz_score_<id>` -> `<id>`. Returns "" for anything else. */
export function quizIdFromScoreSource(sourceFieldId: string): string {
  return isQuizScoreSource(sourceFieldId)
    ? sourceFieldId.slice(QUIZ_SCORE_SOURCE_PREFIX.length)
    : "";
}

/**
 * True when a rule names a quiz that is no longer on the flow. Deleting a quiz
 * component deletes the quiz, and nothing rewrites the rules that pointed at
 * it: the source select renders blank and the rule can never be true again, so
 * every editor that offers quiz sources checks this before saving.
 */
export function isDanglingQuizSource(
  sourceFieldId: string,
  quizzes: Array<{ id: string }>,
): boolean {
  // QL-361 — the score source names the same quiz component as the result source.
  if (!isQuizResultSource(sourceFieldId) && !isQuizScoreSource(sourceFieldId)) return false;
  const quizId = quizIdFromResultSource(sourceFieldId) || quizIdFromScoreSource(sourceFieldId);
  return !quizzes.some((quiz) => quiz.id === quizId);
}

/** Any `__…` id is a sentinel (never a flow-field UUID) — same rule the clone remapper uses. */
export function isSentinelSource(sourceFieldId: string): boolean {
  return sourceFieldId.startsWith("__");
}

/**
 * Derive a source's scope from its id.
 * - sentinel → registry; `__custom_field_<uuid>` → global;
 * - unknown sentinel → global (existing behaviour, deliberately not tightened);
 * - anything else is a flow-field id → `lookupFieldScope`, unknown field → global
 *   (matches the `flow_field.scope` column default).
 */
export function resolveConditionSourceScope(
  sourceFieldId: string,
  lookupFieldScope: (fieldId: string) => ConditionScope | undefined,
): ConditionScope {
  if (isSentinelSource(sourceFieldId)) {
    if (isCustomFieldSource(sourceFieldId)) return "global";
    if (isQuizResultSource(sourceFieldId)) return "global";
    if (isQuizScoreSource(sourceFieldId)) return "global";
    if (isProductAssessmentSource(sourceFieldId)) return "global";
    return SENTINEL_SOURCE_SCOPES[sourceFieldId] ?? "global";
  }
  return lookupFieldScope(sourceFieldId) ?? "global";
}

/** PDF section scope → container scope for the predicate. Attachments are always `global`. */
export function pdfSectionScopeToConditionScope(sectionScope: PdfSectionScope): ConditionScope {
  switch (sectionScope) {
    case "per_goal":
      return "goal";
    case "per_portfolio":
      return "portfolio";
    default:
      return "global";
  }
}

/**
 * Self-referential conditional display guard.
 *
 * Sources a component type is the sole in-flow writer of. A component
 * conditioned on its own output can never legitimately toggle itself: either
 * the source starts empty/default and the component deadlocks hidden (it can
 * never produce the value that would show it — e.g. client_classification on
 * `__classification_type__`), or the value pre-exists and the component
 * trap-doors (changing the value hides the only input that could change it
 * back — e.g. portfolio_input on `__portfolio_type__`, default "model").
 *
 * Registry + predicate are the single source of truth shared by the
 * flow-editor pickers (UX filtering) and the server-side component write
 * guard (authoritative), so they cannot drift. Conditioning OTHER components
 * on these sources stays fully allowed — that is their purpose.
 */
export const COMPONENT_PRODUCED_SOURCES: Readonly<Record<string, readonly string[]>> = {
  client_classification: [CLASSIFICATION_TYPE_SOURCE],
  trapets_lookup: [TRAPETS_PEP_SOURCE, TRAPETS_SANCTIONED_SOURCE],
  sumsub_verification: [SUMSUB_VERIFIED_SOURCE, SUMSUB_DOCUMENT_COUNTRY_SOURCE],
  financial_situation: [EXPECTING_FINANCIAL_CHANGES_SOURCE],
  risk_assessment: [GOAL_RISK_SCORE_SOURCE, GOAL_HAS_RISK_SOURCE, GOAL_RISK_SCORE_CAPPED_SOURCE],
  mandate: [
    GOAL_HAS_MANDATE_SOURCE,
    PORTFOLIO_HAS_MANDATE_CHECK_SOURCE,
    PORTFOLIO_MANDATE_STATUS_SOURCE,
  ],
  goal_input: [GOAL_TYPE_SOURCE],
  portfolio_input: [PORTFOLIO_TYPE_SOURCE, PORTFOLIO_SCHEDULE_SOURCE],
};

/**
 * Whether `sourceFieldId` is produced by the component itself. The `field`
 * case compares against the component's own `flowFieldId`; widget components
 * check the produced-sources registry. Unknown component types are never
 * self-referential (deny only on a positive match).
 *
 * QL-361 — two producers cannot be expressed as a fixed id list:
 * - `quiz` produces only its OWN quiz's result/score source (the quiz id is
 *   the component id), so other quizzes on the flow stay available. Without
 *   `ownComponentId` (create — no id yet) it cannot reference itself.
 * - `product_assessment` produces every `__pa_…` source: a flow has one
 *   collection, so all of them come from that component.
 */
export function isSelfReferentialConditionSource(
  componentType: string,
  sourceFieldId: string,
  ownFlowFieldId?: string | null,
  ownComponentId?: string | null,
): boolean {
  if (componentType === "field") {
    return !!ownFlowFieldId && sourceFieldId === ownFlowFieldId;
  }
  if (componentType === "quiz") {
    return (
      !!ownComponentId &&
      (sourceFieldId === `${QUIZ_RESULT_SOURCE_PREFIX}${ownComponentId}` ||
        sourceFieldId === `${QUIZ_SCORE_SOURCE_PREFIX}${ownComponentId}`)
    );
  }
  if (componentType === "product_assessment") {
    return isProductAssessmentSource(sourceFieldId);
  }
  return (COMPONENT_PRODUCED_SOURCES[componentType] ?? []).includes(sourceFieldId);
}
